'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { TableEmptyState, type TableEmptyStateProps } from './table-empty-state'
import { Button } from './button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu'
import { MoreHorizontal, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface DataTableColumn<T> {
  /** Column key for accessing data */
  key: string
  /** Column header label */
  header: string
  /** Custom render function */
  render?: (item: T, index: number) => React.ReactNode
  /** Column width class */
  width?: string
  /** Text alignment */
  align?: 'start' | 'center' | 'end'
  /** Enable sorting for this column */
  sortable?: boolean
  /** Custom sort key (defaults to column key) */
  sortKey?: string
}

export interface DataTableAction<T> {
  /** Action label */
  label: string
  /** Action icon */
  icon?: LucideIcon
  /** Action handler */
  onClick: (item: T) => void
  /** Is destructive action (shows in red) */
  destructive?: boolean
  /** Separator before this action */
  separator?: boolean
  /** Conditionally hide this action */
  hidden?: (item: T) => boolean
}

/** Sort order type */
export type SortOrder = 'asc' | 'desc'

export interface DataTableProps<T> {
  /** Table data */
  data: T[]
  /** Column definitions */
  columns: DataTableColumn<T>[]
  /** Row actions */
  actions?: DataTableAction<T>[]
  /** Get unique key for each row */
  getRowKey: (item: T) => string
  /** Row click handler */
  onRowClick?: (item: T) => void
  /** Row double click handler */
  onRowDoubleClick?: (item: T) => void
  /** Currently selected row key */
  selectedRowKey?: string | null
  /** Empty state props */
  emptyState?: TableEmptyStateProps
  /** Is RTL mode */
  isRTL?: boolean
  /** Additional className */
  className?: string
  /** Current sort field */
  sortField?: string
  /** Current sort order */
  sortOrder?: SortOrder
  /** Sort change handler */
  onSort?: (field: string, order: SortOrder) => void
}

/**
 * Unified data table component
 * Provides consistent table styling with row selection, actions, and empty state
 */
export function DataTable<T>({
  data,
  columns,
  actions,
  getRowKey,
  onRowClick,
  onRowDoubleClick,
  selectedRowKey,
  emptyState,
  isRTL = false,
  className,
  sortField,
  sortOrder,
  onSort,
}: DataTableProps<T>) {
  // Handle sort click
  const handleSortClick = (column: DataTableColumn<T>) => {
    if (!column.sortable || !onSort) return

    const key = column.sortKey ?? column.key
    const isCurrentField = sortField === key

    // Toggle: none -> asc -> desc -> none
    let newOrder: SortOrder = 'asc'
    if (isCurrentField) {
      if (sortOrder === 'asc') {
        newOrder = 'desc'
      } else if (sortOrder === 'desc') {
        // Reset to default (asc) or could clear sort
        newOrder = 'asc'
      }
    }

    onSort(key, newOrder)
  }

  // Get sort icon for column
  const getSortIcon = (column: DataTableColumn<T>) => {
    if (!column.sortable) return null

    const key = column.sortKey ?? column.key
    const isCurrentField = sortField === key

    if (!isCurrentField) {
      return <ArrowUpDown className="w-3.5 h-3.5 opacity-0 group-hover/sortable:opacity-50 transition-opacity" />
    }

    if (sortOrder === 'asc') {
      return <ArrowUp className="w-3.5 h-3.5 text-primary" />
    }

    return <ArrowDown className="w-3.5 h-3.5 text-primary" />
  }

  if (data.length === 0 && emptyState) {
    return (
      <div className="glass-card rounded-2xl overflow-hidden">
        <TableEmptyState {...emptyState} />
      </div>
    )
  }

  return (
    <div className={cn(
      "glass-card rounded-2xl overflow-hidden border border-border/50",
      className
    )}>
      <Table>
        <TableHeader>
          <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
            {columns.map((column) => {
              const isSortable = column.sortable && onSort
              const sortKey = column.sortKey ?? column.key
              const isActiveSortField = sortField === sortKey

              return (
                <TableHead
                  key={column.key}
                  className={cn(
                    "text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider",
                    column.width,
                    column.align === 'end' && "text-end",
                    column.align === 'center' && "text-center",
                    isSortable && "cursor-pointer select-none group/sortable hover:text-foreground transition-colors",
                    isActiveSortField && "text-foreground"
                  )}
                  onClick={isSortable ? () => handleSortClick(column) : undefined}
                >
                  <div className={cn(
                    "flex items-center gap-1.5",
                    column.align === 'end' && "justify-end flex-row-reverse",
                    column.align === 'center' && "justify-center"
                  )}>
                    <span>{column.header}</span>
                    {getSortIcon(column)}
                  </div>
                </TableHead>
              )
            })}
            {actions && actions.length > 0 && (
              <TableHead className="text-muted-foreground font-bold px-5 py-4 text-[11px] uppercase tracking-wider text-end w-[100px]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => {
            const rowKey = getRowKey(item)
            const isSelected = selectedRowKey === rowKey

            return (
              <TableRow
                key={rowKey}
                className={cn(
                  "border-border/30 cursor-pointer group transition-all duration-150",
                  isSelected
                    ? "bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20"
                    : "hover:bg-muted/50"
                )}
                onClick={() => onRowClick?.(item)}
                onDoubleClick={() => onRowDoubleClick?.(item)}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      "px-5 py-4",
                      column.align === 'end' && "text-end",
                      column.align === 'center' && "text-center"
                    )}
                  >
                    {column.render
                      ? column.render(item, index)
                      : String((item as Record<string, unknown>)[column.key] ?? '')}
                  </TableCell>
                ))}
                {actions && actions.length > 0 && (
                  <TableCell className="px-5 py-4 text-end">
                    <div className="flex items-center justify-end gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-muted">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-48 rounded-xl bg-white dark:bg-slate-900 border border-border shadow-lg"
                        >
                          {actions.map((action, actionIndex) => {
                            const Icon = action.icon
                            if (action.hidden?.(item)) {
                              return null
                            }
                            return (
                              <React.Fragment key={actionIndex}>
                                {action.separator && <DropdownMenuSeparator />}
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    action.onClick(item)
                                  }}
                                  className={cn(
                                    "rounded-lg",
                                    action.destructive && "text-destructive focus:text-destructive"
                                  )}
                                >
                                  {Icon && <Icon className="w-4 h-4 me-2" />}
                                  {action.label}
                                </DropdownMenuItem>
                              </React.Fragment>
                            )
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
